/**
 * The single route table of the storefront SPA.
 *
 * Every in-app page has exactly one canonical path. Paths that belong to
 * separately served documents (the /go/ landing pages, /academy/, /oraculum/,
 * /cart …) are not routes: navigating to them is an ordinary full-page load.
 */
export const PAGE_PATHS = {
  home: '/home',
  'sovereign-stack': '/sovereign-stack',
  'ohm-gear': '/ohm-gear',
  'shadow-king': '/shadow-king',
  scientia: '/scientia',
  'nostram-missionem': '/nostram-missionem',
  arcana: '/arcana',
  kybalion: '/kybalion',
  contact: '/contact',
  'sign-in': '/sign-in',
  privacy: '/privacy',
  terms: '/terms',
  refunds: '/refunds',
  shipping: '/shipping',
  disclaimer: '/disclaimer',
} as const;

export type StaticPage = keyof typeof PAGE_PATHS;
export type Page = StaticPage | 'product' | 'not-found';

export interface Route {
  readonly page: Page;
  /** Product handle, present only when page === 'product'. */
  readonly handle?: string;
}

/** Full-page destinations outside the SPA (served as their own documents). */
export const EXTERNAL_PATHS = {
  shadowKingLanding: '/go/shadow-king',
  shadowOracle: '/shadow-oracle/',
  /** SYSTEMA's one sales page. The retired /ohm-os/ answers with a 301 to it (public/.htaccess). */
  systemaLanding: '/go/systema/',
  sibylla: '/sibylla/',
  systema: '/systema/',
  academy: '/academy/',
  oraculum: '/oraculum/',
  instrumentarium: '/instrumentarium/',
  praesidium: '/praesidium/',
  cart: '/cart',
  recover: '/recover/',
  /** The bookstore library: an owner downloads the Book of Enoch's digital edition here (ΩΞ-TILL-BOOKS-001). */
  enochLibrary: '/library/enoch/',
} as const;

/**
 * FN-D14: SPA locations that belong to a static surface. The superseded SPA pages are not ported;
 * the location is replaced by the surface's canonical (slash) URL. Keys have no trailing slash.
 * ΩΞ-REBUILD-001-A7 (S1): /ohm-os is retired — it is no route and no surface; the edge 301s it to /go/systema/.
 */
const STATIC_SURFACES = new Map<string, string>([['/shadow-oracle', EXTERNAL_PATHS.shadowOracle]]);

/** The static surface URL an SPA pathname hands over to (trailing slashes ignored), or null. */
export function staticSurfaceFor(pathname: string): string | null {
  return STATIC_SURFACES.get(pathname.replace(/\/+$/, '')) ?? null;
}

const PATH_TO_PAGE = new Map<string, StaticPage>(
  (Object.entries(PAGE_PATHS) as [StaticPage, string][]).map(([page, path]) => [path, page]),
);

function decodeHandle(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

export function parseLocation(pathname: string): Route {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const product = /^\/product\/([^/]+)$/.exec(trimmed);
  if (product?.[1] !== undefined) {
    const handle = decodeHandle(product[1]);
    return handle === null ? { page: 'not-found' } : { page: 'product', handle };
  }
  const page = PATH_TO_PAGE.get(trimmed);
  return { page: page ?? 'not-found' };
}

export function pathFor(route: Route): string {
  if (route.page === 'product') return `/product/${encodeURIComponent(route.handle ?? '')}`;
  if (route.page === 'not-found') return PAGE_PATHS.home;
  return PAGE_PATHS[route.page];
}
